import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  LinearProgress,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import { ArrowBack, ArrowForward, Send } from '@mui/icons-material';
// import { testSessionAPI } from '@/services/apiService';
import { writingAPI } from '../services/apiServices';
// import AiTutor from '@/components/AiTutor/AiTutor';

const TestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [startTime] = useState(Date.now());
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if(!id) return;
    const fetchTest = async () => {
      try {
        const data = await writingAPI.getSession(id);
        setTest(data.writingSession);
        if (data.writingSession.timed && data.writingSession.timeLimit) {
          setTimeRemaining(data.writingSession.timeLimit * 60); // Convert minutes to seconds
        }
        setLoading(false);
      } catch (error) {
        toast.error(error.message);
        navigate('/');
      }
    };
    fetchTest();
  }, [id]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
      
      if (timeRemaining !== null) {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            clearInterval(timer);
            handleSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, timeRemaining]);

  const handleAnswer = (answer) => {
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentQuestion] = answer;
      return newAnswers;
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Check if this is a creative or persuasive writing test
      const isCreativeOrPersuasive = 
        test.testType === 'creative_writing' || 
        test.testType === 'persuasive_writing';
      
      let result;
      
      if (isCreativeOrPersuasive) {
        // For creative/persuasive writing, we submit the single answer
        result = await writingAPI.submitCreativePersuasiveSession(
          id, 
          answers[currentQuestion] || ''
        );
      } else {
        // For other writing types, we submit all answers as an array
        result = await writingAPI.submitSession(id, answers);
      }
      
      toast.success('Test submitted successfully!');
      navigate(`/writing-results/${result.testResult._id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderQuestionContent = () => {
    const currentQ = test.questions[currentQuestion];
    const isCreativeMode = test.testType === 'creative_writing';
    const isPersuasiveMode = test.testType === 'persuasive_writing';
    const isStoryType = test.testType === 'short_story';

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {isCreativeMode 
                ? 'Writing Prompt:' 
                : isPersuasiveMode 
                  ? 'Persuasive Topic:' 
                  : isStoryType 
                    ? 'Story to Correct:' 
                    : 'Question:'}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {isCreativeMode || isPersuasiveMode ? 'Topic' : 'Topic'}: {test.topic}
            </Typography>
            {!isCreativeMode && !isPersuasiveMode && (
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                }}
              >
                {currentQ.question}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {isCreativeMode 
                ? 'Your Story:' 
                : isPersuasiveMode 
                  ? 'Your Argument:' 
                  : 'Your Correction:'}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={isCreativeMode || isPersuasiveMode || isStoryType ? 12 : 4}
              value={answers[currentQuestion] || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder={
                isCreativeMode 
                  ? "Write your story here..."
                  : isPersuasiveMode
                    ? "Write your persuasive argument here..."
                    : isStoryType 
                      ? "Rewrite the story with corrections..." 
                      : "Write your corrected version here..."
              }
              variant="outlined"
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4">
              {test.testType === 'creative_writing' 
                ? 'Creative Writing'
                : test.testType === 'persuasive_writing'
                  ? 'Persuasive Writing'
                  : test.testType === 'short_story' 
                    ? 'Story Correction' 
                    : 'Sentence Correction'}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Grade {test.gradeWhenStart} {test.testType === 'persuasive_writing' || test.testType === 'creative_writing' ? "" : `- ${test.topic}`}
            </Typography>
          </Box>
          <Typography variant="h6">
            {test.timed 
              ? `Time Remaining: ${formatTime(timeRemaining)}` 
              : `Time: ${formatTime(timeElapsed)}`}
          </Typography>
        </Box>

        {test.testType === 'short_sentences' && (
          <Stepper activeStep={currentQuestion} alternativeLabel sx={{ mb: 4 }}>
            {test.questions.map((_, index) => (
              <Step key={index}>
                <StepLabel>Question {index + 1}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {renderQuestionContent()}

        <Box display="flex" justifyContent="space-between">
          {test.testType === 'short_sentences' && (
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setCurrentQuestion(prev => prev - 1)}
              disabled={currentQuestion === 0 || isSubmitting}
              variant="outlined"
            >
              Previous
            </Button>
          )}
          
          {currentQuestion === test.questions.length - 1 ? (
            <Button
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              fullWidth={test.testType !== 'short_sentences'}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Test'}
            </Button>
          ) : (
            <Button
              endIcon={<ArrowForward />}
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              variant="contained"
              disabled={!answers[currentQuestion] || isSubmitting}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
      {/* <AiTutor
        currentQuestion={test?.questions[currentQuestion]}
        userAnswer={answers[currentQuestion]}
        isAnswerWrong={answers[currentQuestion] && !test?.questions[currentQuestion]?.isCorrect}
      /> */}
    </Container>
  );
};

export default TestPage; 